import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { useCar } from '@/contexts/CarContext';
import { useCarSelectionContext } from '@/contexts/CarSelectionContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, Plus, X, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';

const AddTaskPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cars } = useCar();
  const { currentCar } = useCarSelectionContext();
  const { toast } = useToast();
  const { t, language } = useI18n();

  // Form state
  const [selectedCarId, setSelectedCarId] = useState<string>('');
  const [odometerReading, setOdometerReading] = useState<string>('');
  const [lastOdometerReading, setLastOdometerReading] = useState<number | null>(null);
  const [tasks, setTasks] = useState<Array<{ description: string; due: string }>>([{ description: '', due: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [mode, setMode] = useState<'km' | 'time'>('km');
  const [km, setKm] = useState<string>('');
  const [time, setTime] = useState<string>('');
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [isRemoveMode, setIsRemoveMode] = useState(false);
  const [currentOdometer, setCurrentOdometer] = useState<number | null>(null);
  const [showOdometerInfo, setShowOdometerInfo] = useState(false);

  // Get car from URL params or current selection
  const carIdFromParams = searchParams.get('carId');
  const defaultCarId = carIdFromParams || currentCar?.id || '';

  useEffect(() => {
    if (defaultCarId && cars.find(c => c.id === defaultCarId)) {
      setSelectedCarId(defaultCarId);
    }
  }, [defaultCarId, cars]);

  // Fetch last odometer reading when car changes
  useEffect(() => {
    if (selectedCarId) {
      const fetchLastOdometer = async () => {
        try {
          const { data } = await supabase
            .from('odometer_readings')
            .select('reading')
            .eq('car_id', selectedCarId)
            // Latest = highest reading value (regardless of date); if equal, newest date wins
            .order('reading', { ascending: false })
            .order('recorded_at', { ascending: false })
            .limit(10); // Fetch top 10 to check for outliers
          
          if (data && data.length > 0) {
            const { filterOdometerOutliers } = await import('@/lib/utils');
            const reading = filterOdometerOutliers(data);
            if (reading !== null) {
              setLastOdometerReading(reading);
              setCurrentOdometer(reading);
            } else {
              setLastOdometerReading(null);
              setCurrentOdometer(null);
            }
          } else {
            setLastOdometerReading(null);
            setCurrentOdometer(null);
          }
        } catch (error) {
          console.error('Error fetching last odometer reading:', error);
          setLastOdometerReading(null);
          setCurrentOdometer(null);
        }
      };
      
      fetchLastOdometer();
    }
  }, [selectedCarId]);

  // Reset remove mode when there are less than 2 tasks
  useEffect(() => {
    if (tasks.length < 2 && isRemoveMode) {
      setIsRemoveMode(false);
    }
  }, [tasks.length, isRemoveMode]);

  const openTaskDrawer = (index: number) => {
    setActiveIndex(index);
    const currentTask = tasks[index];
    
    // Parse existing due value to set mode and values
    if (currentTask.due?.includes('km')) {
      setMode('km');
      const kmValue = currentTask.due.replace(/\s*km\s*/g, '');
      setKm(kmValue);
      setTime('');
    } else if (currentTask.due) {
      setMode('time');
      setTime(currentTask.due);
      setKm('');
    } else {
      setMode('km');
      setKm('');
      setTime('');
    }
    
    setTaskDrawerOpen(true);
  };

  const handleTaskDescriptionChange = (text: string) => {
    if (activeIndex == null) return;
    setTasks((prev) => {
      const copy = [...prev];
      copy[activeIndex].description = text;
      return copy;
    });
  };

  const handleIntervalAdd = () => {
    if (activeIndex == null) return;
    const value = mode === 'km' ? (km ? `${km} km` : '') : time;
    setTasks((prev) => {
      const copy = [...prev];
      copy[activeIndex].due = value;
      return copy;
    });
    setTaskDrawerOpen(false);
  };

  const addTaskRow = () => {
    setTasks((prev) => [...prev, { description: '', due: '' }]);
  };

  const removeTaskRow = (index: number) => {
    setTasks((prev) => {
      if (prev.length <= 1) {
        // Keep at least one empty field
        return [{ description: '', due: '' }];
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const clearTaskField = (index: number) => {
    setTasks((prev) => {
      const copy = [...prev];
      copy[index] = { description: '', due: '' };
      return copy;
    });
  };

  // Parse interval string into due_date and due_odometer
  const parseInterval = (interval: string): { due_date: string | null; due_odometer: number | null } => {
    if (!interval || !interval.trim()) {
      return { due_date: null, due_odometer: null };
    }

    const trimmed = interval.trim();

    // Use entered odometer reading if available, otherwise use current odometer
    const baseOdometer = odometerReading ? parseInt(odometerReading, 10) : (currentOdometer || null);

    // Check for KM-based intervals (e.g., "5000 km", "10000 km")
    const kmMatch = trimmed.match(/^(\d+)\s*km$/i);
    if (kmMatch && baseOdometer !== null && !Number.isNaN(baseOdometer)) {
      const kmValue = parseInt(kmMatch[1], 10);
      if (!Number.isNaN(kmValue) && kmValue > 0) {
        return {
          due_date: null,
          due_odometer: baseOdometer + kmValue,
        };
      }
    }

    // Check for time-based intervals (use stable keys so parsing works in any locale)
    const now = new Date();
    let dueDate: Date | null = null;

    if (trimmed === 'next_visit' || trimmed.toLowerCase() === 'next visit' || trimmed.toLowerCase() === 'nextvisit') {
      return { due_date: null, due_odometer: null };
    }

    if (trimmed === 'next_year' || trimmed.toLowerCase() === 'next year' || trimmed.toLowerCase() === 'nextyear') {
      dueDate = new Date(now.getFullYear() + 1, 0, 1);
    } else {
      const inMonthsMatch = trimmed.match(/^in_(\d+)_months$/);
      if (inMonthsMatch) {
        const months = parseInt(inMonthsMatch[1], 10);
        if (!Number.isNaN(months) && months > 0) {
          dueDate = new Date(now);
          dueDate.setMonth(now.getMonth() + months);
        }
      } else {
        const monthMatch = trimmed.match(/^in\s+(\d+)\s+month(?:s)?$/i);
        if (monthMatch) {
          const months = parseInt(monthMatch[1], 10);
          if (!Number.isNaN(months) && months > 0) {
            dueDate = new Date(now);
            dueDate.setMonth(now.getMonth() + months);
          }
        }
      }
    }

    return {
      due_date: dueDate ? dueDate.toISOString().split('T')[0] : null,
      due_odometer: null,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Filter out empty tasks
    const validTasks = tasks.filter(t => t.description.trim());
    
    if (!selectedCarId || validTasks.length === 0) {
      setError(t('addTask.errorRequired') || 'Please fill in at least one task');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Parse intervals and prepare tasks for insertion
      const tasksToInsert = validTasks.map(task => {
        const { due_date, due_odometer } = parseInterval(task.due);
        return {
          car_id: selectedCarId,
          user_id: user.id,
          title: task.description.trim(),
          due_date,
          due_odometer,
          status: 'pending',
          source: 'manual',
          notes: null,
        };
      });

      // Insert all tasks
      const { error: insertError } = await supabase
        .from('tasks')
        .insert(tasksToInsert);

      if (insertError) {
        throw insertError;
      }

      // Save odometer reading if provided
      if (odometerReading && selectedCarId) {
        const readingValue = parseInt(odometerReading, 10);
        if (!Number.isNaN(readingValue) && readingValue > 0) {
          await supabase.from('odometer_readings').insert({
            car_id: selectedCarId,
            entered_by: user.id,
            reading: readingValue,
            recorded_at: new Date().toISOString(),
            source: 'manual',
          });
        }
      }

      // Show success dialog
      setSuccessDialogOpen(true);
    } catch (err) {
      console.error('Error adding tasks:', err);
      setError(t('addTask.error') || 'Failed to add tasks. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCar = cars.find(c => c.id === selectedCarId);
  const currentTask = activeIndex !== null ? tasks[activeIndex] : null;

  return (
    <>
      <PageLayout
        title={t('addTask.title') || 'Add Task'}
        showBackButton
        onBack={() => navigate(-1)}
      >
        <div className="max-w-5xl mx-auto px-1 py-2">
          <form onSubmit={handleSubmit} className="pb-24" aria-describedby={error ? 'form-error' : undefined}>
            <div className="space-y-4">
              {/* Car Selection */}
              <div className="rounded-2xl bg-white shadow-md border border-gray-100 p-4">
                <Label htmlFor="car" className="text-sm text-gray-600 mb-2 block">
                  {t('addTask.car') || 'Car'} <span className="text-gray-600">*</span>
                </Label>
                <Select value={selectedCarId} onValueChange={setSelectedCarId} required>
                  <SelectTrigger className="h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm">
                    <SelectValue placeholder={t('addTask.selectCar') || 'Select a car'} />
                  </SelectTrigger>
                  <SelectContent>
                    {cars.map((car) => (
                      <SelectItem key={car.id} value={car.id}>
                        {car.nickname || `${car.brand} ${car.model}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Odometer Reading */}
              <div className="rounded-2xl bg-white shadow-md border border-gray-100 p-4">
                <div className="flex items-center gap-1 mb-2">
                  <Label htmlFor="odometer" className="text-sm text-gray-600">
                    {t('addTask.odometer') || 'Odometer (km)'}
                  </Label>
                  <button
                    type="button"
                    onClick={() => setShowOdometerInfo(true)}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </div>
                <Input
                  id="odometer"
                  type="number"
                  min="0"
                  value={odometerReading}
                  onChange={(e) => setOdometerReading(e.target.value)}
                  placeholder={lastOdometerReading ? `${t('addTask.lastOdo') || 'Last'}: ${lastOdometerReading.toLocaleString()} km` : (t('common.optional') || 'Optional')}
                  className="h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>

              {/* Tasks Section */}
              <div className="rounded-2xl bg-white shadow-md border border-gray-100 p-4">
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm text-gray-600">
                    {t('addTask.tasks') || 'Tasks'} <span className="text-gray-600">*</span>
                  </Label>
                  <span className="text-xs text-gray-600">{t('addTask.setReminder') || 'Set reminder'}</span>
                </div>
                {tasks.map((task, i) => (
                  <div key={i} className="flex gap-2 items-center mb-2">
                    <div className="flex-1 relative">
                      <Input
                        placeholder={t('addTask.whatTask') || 'What task needs to be done?'}
                        value={task.description}
                        readOnly
                        onClick={() => openTaskDrawer(i)}
                        className="cursor-pointer"
                        style={{ fontSize: '0.875rem' }}
                      />
                      {task.description && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearTaskField(i);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          aria-label={t('actions.clear') || 'Clear'}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => openTaskDrawer(i)}
                      className="flex-shrink-0 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                      aria-label={task.due ? t('addTask.editReminder') || 'Edit reminder' : t('addTask.addReminder') || 'Add reminder'}
                    >
                      <img
                        src={task.due ? '/icons/AddServiceEntry/Reminder.png' : '/icons/AddServiceEntry/Add reminder.png'}
                        alt={task.due ? (t('addTask.editReminder') || 'Edit reminder') : (t('addTask.addReminder') || 'Add reminder')}
                        className="w-8 h-8"
                      />
                    </button>
                    {isRemoveMode && tasks.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTaskRow(i)}
                        className="flex-shrink-0 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        aria-label={t('actions.delete') || 'Delete'}
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                  </div>
                ))}
                <div className="flex items-center mt-8 mb-8 relative min-h-[2.5rem]">
                  {tasks.length >= 2 && (
                    <button
                      type="button"
                      onClick={() => {
                        if (isRemoveMode) {
                          setIsRemoveMode(false);
                        } else {
                          setIsRemoveMode(true);
                        }
                      }}
                      className={`text-xs transition-colors ${
                        isRemoveMode
                          ? 'text-red-600 underline'
                          : 'text-gray-600 hover:text-gray-800 underline'
                      }`}
                    >
                      {t('actions.remove') || 'Remove'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={addTaskRow}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full border-2 border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 flex items-center justify-center transition-colors"
                    aria-label={t('actions.add') || 'Add'}
                  >
                    <Plus className="w-5 h-5 text-gray-700" />
                  </button>
                </div>
              </div>

              {error && (
                <div id="form-error" className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>

            {/* Sticky footer actions */}
            <div className="fixed left-0 right-0 bottom-16 z-50 border-t border-gray-200 bg-white">
              <div className="mx-auto max-w-5xl px-1 py-1.5 flex gap-1.5">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate(-1)}
                  className="flex-1 rounded-xl border border-gray-300 text-xs py-2 h-9"
                >
                  {t('actions.cancel') || 'Cancel'}
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={submitting || !selectedCarId || tasks.filter(t => t.description.trim()).length === 0}
                  className="flex-1 rounded-xl bg-[#7c9ffa] hover:bg-[#6b8ce8] text-xs py-2 h-9"
                >
                  {submitting ? (
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 animate-spin" />
                      <span>{t('common.adding') || 'Adding...'}</span>
                    </div>
                  ) : (
                    t('actions.finish') || 'Finish'
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </PageLayout>

      {/* Combined Task Description and Interval Drawer */}
      {taskDrawerOpen && activeIndex !== null && currentTask && (
        <div className="fixed inset-0 bg-black/30 flex justify-center items-end z-50" role="dialog" aria-modal="true">
          <div className="bg-white w-full max-w-md rounded-t-2xl p-4 shadow-xl max-h-[90vh] flex flex-col overflow-y-auto">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-sm font-semibold text-gray-800">
                {t('addTask.addTask') || 'Add Task'}
              </h2>
              <button
                onClick={() => setTaskDrawerOpen(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label={t('actions.close')}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            
            {/* Task Description */}
            <div className="mb-4">
              <Label className="text-xs text-gray-600 mb-1 block">
                {t('addTask.taskDescription') || 'Task Description'} <span className="text-gray-600">*</span>
              </Label>
              <Textarea
                placeholder={t('addTask.startTyping') || 'Start typing...'}
                value={currentTask.description}
                onChange={(e) => handleTaskDescriptionChange(e.target.value)}
                className="w-full resize-none text-sm"
                rows={4}
              />
            </div>

            {/* Interval Selection */}
            <div className="mb-4">
              <Label className="text-xs text-gray-600 mb-1 block">
                {t('addTask.setReminder') || 'Set Reminder'} ({t('common.optional') || 'Optional'})
              </Label>
              <p className="text-xs text-gray-500 mb-3">
                {t('addTask.intervalHelper') || 'Choose how to measure when this task is due — by kilometers or time.'}
              </p>
              <div className="flex rounded-full border border-gray-200 bg-gray-100 text-xs font-medium h-[32px] mb-3">
                {(['km', 'time'] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setMode(opt)}
                    className={`flex-1 px-3 rounded-full transition ${mode === opt ? 'bg-[#e7fe52] text-gray-900' : 'text-gray-500'}`}
                  >
                    {opt === 'km' ? (t('addTask.intervalKm') || 'KM') : (t('addTask.intervalTime') || 'Time')}
                  </button>
                ))}
              </div>
              {mode === 'km' ? (
                <select
                  className="w-full h-[32px] mb-2 rounded-xl border border-gray-200 bg-[#fafafa] px-2 text-sm focus:ring-2 focus:ring-[#7097fa]/30 focus:border-[#7097fa]/40"
                  value={km}
                  onChange={(e) => setKm(e.target.value)}
                >
                  <option value="">{t('addTask.selectKm') || 'Select KM Interval'}</option>
                  {Array.from({ length: 10 }, (_, i) => (i + 1) * 5000).map((v) => (
                    <option key={v} value={v}>
                      {t('addTask.inKm', { value: v }) || `In ${v} km`}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  className="w-full h-[32px] mb-2 rounded-xl border border-gray-200 bg-[#fafafa] px-2 text-sm focus:ring-2 focus:ring-[#7097fa]/30 focus:border-[#7097fa]/40"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                >
                  <option value="">{t('addTask.selectTime') || 'Select Time Option'}</option>
                  <option value="next_visit">{t('addTask.nextVisit') || 'Next visit'}</option>
                  <option value="next_year">{t('addTask.nextYear') || 'Next year'}</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={`in_${m}_months`}>{t('addTask.inMonths', { value: m }) || `In ${m} month(s)`}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="flex justify-between gap-2 pt-3 border-t border-gray-100">
              <button
                onClick={() => setTaskDrawerOpen(false)}
                className="text-sm text-gray-700 bg-gray-100 px-4 py-2 rounded-lg"
              >
                {t('actions.cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleIntervalAdd}
                disabled={!currentTask.description.trim()}
                className="text-sm text-gray-900 bg-[#e7fe52] px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('actions.add') || 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Odometer Info Dialog */}
      <Dialog open={showOdometerInfo} onOpenChange={setShowOdometerInfo}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {t('addTask.odometer') || 'Odometer Reading'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              {t('addTask.odometerInfo') || 'Odometer reading is helpful for calculating task due dates based on mileage.'}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
        <DialogContent className="sm:max-w-[360px] p-6">
          <div className="text-center">
            <img src="/icons/Success.png" alt={t('common.success')} className="w-12 h-12 mx-auto mb-3" />
            <DialogTitle className="text-lg font-semibold mb-2">
              {t('addTask.successTitle') || 'Tasks Added'}
            </DialogTitle>
            <p className="text-sm text-gray-600 mb-6">
              {t('addTask.successDesc') || 'Your tasks have been successfully added.'}
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => {
                  setSuccessDialogOpen(false);
                  navigate(`/records/${selectedCarId}?tab=tasks`);
                }}
                className="w-full bg-[#e7fe52] text-black hover:bg-[#e7fe52]/90"
              >
                {t('addTask.viewTasks') || 'View Tasks'}
              </Button>
              <Button
                onClick={() => {
                  setSuccessDialogOpen(false);
                  navigate(-1);
                }}
                variant="secondary"
                className="w-full"
              >
                {t('actions.close') || 'Close'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddTaskPage;
